<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { getCookie, setCookie } from '@/common/utils/cookie'

import ImageSlider from './ImageSlider.vue'
import InspectionPicker from './InspectionPicker.vue'
import InspectionTimeline from './InspectionTimeline.vue'
import DescrepanceyTable from './DiscrepancyTable.vue'
import { useImageAnnotator } from './useImageAnnotator'
import { ElNotification } from 'element-plus'

// Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import { useMarkerStore } from 'Store/pages/imageReview/markerStore'
// API
import { useGetInspectionImagesFiltered } from 'API/queries/iris/inspection-image'
import { useImageAnnotationsFiltered } from 'API/queries/iris/annotation'

// Types
import { ImageAnnotation, ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
// Utils
import { generateMarkerStateFromAnnotations } from './helperFunctions'
import { formatDayByDate, formatTimeByDate } from '@/common/utils/formatDate'
import { getSortedInspectionDates, getGroupedAnnotationsMap, getGroupedAnnotationsArray } from './helperFunctions'
import { useGetInspectionInstancesFiltered } from 'API/queries/iris/inspection-instance'

interface PropTypes {
  pushImageRoute: (imageUuid: string) => void
  inspectionInstanceUuid
  aircraftData
}
const props = defineProps<PropTypes>()

const store = useImageReviewStore()
const markerStore = useMarkerStore()
const { createMarkerEntry } = markerStore
const { annotations, confirmedAnnotations, groupedAnnotationsMap, groupedAnnotationsArray } = storeToRefs(
  store.annotationsStore
)
const { annotationsLoaded } = storeToRefs(store.serverStore)
const { currentImage } = storeToRefs(store.imagesStore)

// --- Local State ---
const currentImgRef = ref<HTMLImageElement>(null)
const comparisonImgRef = ref<HTMLImageElement>(null)
const currentInstance = ref()
const comparisonInstance = ref()

// State for Current Image
const currentImageSrc = ref<string | null>(null)
const isCurrentLoading = ref(false)

// State for Comparison Image
const comparisonImageForTable = ref(null)
const comparisonImageSrc = ref<string | null>(null)
const isComparisonLoading = ref(false)
const noComparisonImageFound = ref(false)

// --- API Queries ---
const inspectionFilterParams = computed(() => ({
  aircraft: props.aircraftData?.[0]?.uuid ? [props.aircraftData[0].uuid] : [],
}))

const { data: inspectionData } = useGetInspectionInstancesFiltered({
  variables: { filterQueryParams: inspectionFilterParams },
  enabled: computed(() => !!props.aircraftData?.[0]?.uuid),
  refetchOnWindowFocus: false,
})

const comparisonFilterParams = ref({
  aircraft: [],
  waypoint: [],
  inspectionInstance: [],
})

watch(
  [() => props.aircraftData, currentImage, comparisonInstance],
  ([newAircraftData, newCurrentImage, newComparisonInstance]) => {
    comparisonFilterParams.value = {
      aircraft: newAircraftData?.[0]?.uuid ? [newAircraftData[0].uuid] : [],
      waypoint: newCurrentImage?.waypointUuid ? [newCurrentImage.waypointUuid] : [],
      inspectionInstance: newComparisonInstance?.inspectionInstanceUuid
        ? [newComparisonInstance.inspectionInstanceUuid]
        : [],
    }
  },
  { immediate: true, deep: true }
)

const { data: imagesQuery } = useGetInspectionImagesFiltered({
  variables: { filterQueryParams: comparisonFilterParams },
  enabled: computed(
    () => !!(props.aircraftData?.[0]?.uuid && currentImage.value?.waypointUuid && comparisonInstance.value)
  ),
  refetchOnWindowFocus: false,
})

const comparisonAnnotationParams = computed(() => ({
  inspectionImage: comparisonImageForTable.value?.imageUuid ? [comparisonImageForTable.value.imageUuid] : [],
}))

const { data: comparisonAnnotations } = useImageAnnotationsFiltered({
  variables: { filterQueryParams: comparisonAnnotationParams },
  enabled: computed(() => !!comparisonImageForTable.value?.imageUuid),
  refetchOnWindowFocus: false,
})

// --- Annotators & Component Logic ---
const defaultActiveSections = ['defects', 'suggestions']
const activeSections = ref(defaultActiveSections)
const defaultAnnotatorOptions = { isMarkersInteractive: false }
const currentAnnotator = useImageAnnotator(defaultAnnotatorOptions)
const comparisonAnnotator = useImageAnnotator(defaultAnnotatorOptions)

const currentAnnotationData = computed(() => ({ confirmedAnnotations, groupedAnnotationsMap, groupedAnnotationsArray }))

const comparisonConfirmedAnnotations = computed<ImageAnnotationType[]>(() => {
  return comparisonAnnotations.value?.filter((anno: ImageAnnotationType) => anno.isAnnotation && !anno.isTemp) ?? []
})

const comparisonGroupedAnnotationsMap = computed(() => getGroupedAnnotationsMap(comparisonConfirmedAnnotations.value))
const comparisonGroupedAnnotationsArray = computed(() =>
  getGroupedAnnotationsArray(comparisonGroupedAnnotationsMap.value)
)

const comparisonAnnotationData = computed(() => ({
  confirmedAnnotations: comparisonConfirmedAnnotations,
  groupedAnnotationsMap: comparisonGroupedAnnotationsMap,
  groupedAnnotationsArray: comparisonGroupedAnnotationsArray,
}))

const inspectionDates = computed(() => {
  if (!inspectionData.value) return []
  return getSortedInspectionDates(inspectionData.value)
})

// --- Image Load and Error Handlers ---
const handleImageALoaded = () => {
  isCurrentLoading.value = false
  currentAnnotator.setImage(currentImgRef.value)
}

const handleImageAError = () => {
  isCurrentLoading.value = false
}

const handleImageBLoaded = () => {
  isComparisonLoading.value = false
  comparisonAnnotator.setImage(comparisonImgRef.value)
}

const handleImageBError = () => {
  isComparisonLoading.value = false
}

function loadAnnotationMarkers(annotator, annotations: ImageAnnotation[]) {
  const markersState = generateMarkerStateFromAnnotations(annotations)
  markersState.forEach((state) => {
    const marker = annotator.markerArea.loadMarker(state)
    createMarkerEntry(marker.state.meta?.id, marker)
  })
}

// --- Watchers ---

watch(
  currentImage,
  async (newCurrentImage) => {
    currentAnnotator.reset()
    isCurrentLoading.value = true
    currentImageSrc.value = null
    await nextTick()
    currentImageSrc.value = newCurrentImage?.imageUrl ?? null
  },
  { immediate: true }
)

watch(
  imagesQuery,
  async (newImages) => {
    const newComparisonImage = newImages?.[0] ?? null
    comparisonImageForTable.value = newComparisonImage
    comparisonAnnotator.reset()

    // Reset the "not found" state on every new query
    noComparisonImageFound.value = false

    if (newComparisonImage?.imageUrl) {
      // If we found an image, enter the loading state and set the src
      isComparisonLoading.value = true
      comparisonImageSrc.value = null // Clear old image
      await nextTick()
      comparisonImageSrc.value = newComparisonImage.imageUrl // Set new image to trigger download
    } else {
      // If no image was found, exit the loading state and clear the src
      isComparisonLoading.value = false
      comparisonImageSrc.value = null
      // If a comparison was attempted, set the "not found" flag for the UI
      if (comparisonInstance.value) {
        noComparisonImageFound.value = true
      }
    }
  },
  { deep: true }
)

watch([currentAnnotator.ready, annotations], () => {
  if (annotationsLoaded.value && currentAnnotator.ready.value && annotations.value) {
    loadAnnotationMarkers(currentAnnotator, annotations.value)
  }
})

watch([comparisonAnnotator.ready, comparisonAnnotations], () => {
  if (comparisonAnnotator.ready.value && comparisonAnnotations.value) {
    loadAnnotationMarkers(comparisonAnnotator, comparisonAnnotations.value)
  }
})

watch(
  inspectionDates,
  (dates) => {
    if (dates.length > 0) {
      const idx = dates.findIndex((insp) => insp.inspectionInstanceUuid === props.inspectionInstanceUuid[0])
      currentInstance.value = dates[idx]
      if (!comparisonInstance.value) {
        const otherInspections = dates.filter((insp) => insp.inspectionInstanceUuid !== props.inspectionInstanceUuid[0])
        if (otherInspections.length > 0) comparisonInstance.value = otherInspections[0]
      }
    }
  },
  { immediate: true }
)

const EXP_COMPARE_ACKNOWLEDGEMENT = 'EXP_COMPARE_ACKNOWLEDGEMENT'
const notifyMessage = ref()
onMounted(() => {
  const userAcknowledgement = getCookie(EXP_COMPARE_ACKNOWLEDGEMENT)

  if (!userAcknowledgement) {
    notifyMessage.value = ElNotification({
      title: 'Experimental Feature',
      message:
        'The image comparison view is part of an experimental feature. While we strive for accuracy, the values shown may not be 100% precise. Please consider this when using the comparison data for your work.',
      duration: 0,
      type: 'info',
      position: 'bottom-left',
      // onClose: () => {
      //   // since we close on Unmounted, lets detect a route change to prevent user acknowledgment
      //   if (route.name === 'SummaryReport') {
      //     setCookie(EXP_MEASUREMENT_ACKNOWLEDGEMENT, true, 1)
      //   }
      // },
    })
  }
})

onUnmounted(() => {
  currentImgRef.value = null
  comparisonImgRef.value = null
  if (notifyMessage.value) {
    notifyMessage.value.close()
    notifyMessage.value = null
  }
})
</script>
<template>
  <div class="relative h-full grid grid-rows-[minmax(70%,_80%)_auto] grid-cols-1 bg-white">
    <div>
      <div class="relative h-full max-h-full w-full max-w-full" style="color: white">
        <section class="relative h-1/6">
          <InspectionTimeline
            :inspection-instance-uuid="props.inspectionInstanceUuid[0]"
            :inspections="inspectionDates"
            :selected="comparisonInstance"
            @on-selected="(inspection) => (comparisonInstance = inspection)"
          />
        </section>

        <section class="relative max-h-full h-5/6">
          <div class="columns-2 relative h-1/2 pb-2">
            <div class="relative h-full w-full grid content-start">
              <div class="bg-orange-100 p-2 ml-6 mr-6 max-h-12">
                <div v-if="currentInstance" style="color: black">
                  Inspection Instance: {{ formatDayByDate(currentInstance.startDate) }}
                  {{ formatTimeByDate(currentInstance.startDate) }}
                </div>
              </div>
              <div class="px-6 overflow-y-hidden">
                <DescrepanceyTable
                  :image="currentImage"
                  :active-sections="activeSections"
                  :annotations="currentAnnotationData"
                />
              </div>
            </div>
            <div class="relative h-full max-h-full w-full bg-black flex items-center justify-center">
              <div
                v-if="isCurrentLoading"
                class="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50 text-white"
              >
                Loading...
              </div>
              <img
                v-if="currentImageSrc"
                ref="currentImgRef"
                class="h-full w-auto"
                :src="currentImageSrc"
                @load="handleImageALoaded"
                @error="handleImageAError"
              />
            </div>
          </div>

          <div v-if="!comparisonInstance" class="relative h-1/2">
            <div class="p-5 mt-5">
              <el-alert title="No Inspections to compare." type="warning" show-icon :closable="false" />
            </div>
          </div>
          <div v-else class="columns-2 relative h-1/2">
            <div class="relative h-full grid grid-rows_auto content-start">
              <div class="bg-purple-100 p-2 ml-6 mr-6 max-h-14">
                <InspectionPicker
                  v-model="comparisonInstance"
                  :inspections="inspectionDates"
                  :disabled-item="currentInstance"
                />
              </div>
              <div class="px-6 relative overflow-y-hidden">
                <DescrepanceyTable
                  :image="comparisonImageForTable"
                  :active-sections="activeSections"
                  :annotations="comparisonAnnotationData"
                />
              </div>
            </div>
            <div class="relative h-full max-h-full w-full bg-black flex items-center justify-center">
              <div
                v-if="isComparisonLoading"
                class="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50 text-white"
              >
                Loading...
              </div>

              <div v-if="noComparisonImageFound && !isComparisonLoading" class="text-white p-4 text-center">
                No matching comparison image found.
              </div>

              <img
                v-if="comparisonImageSrc"
                ref="comparisonImgRef"
                class="h-full w-auto"
                :src="comparisonImageSrc"
                @load="handleImageBLoaded"
                @error="handleImageBError"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
    <div class="z-10 bg-stone">
      <ImageSlider :push-image-route="props.pushImageRoute" />
    </div>
  </div>
</template>
